/**
 * **The round header** — the trapezoid hanging from the stage's top edge (COMBAT's top bar retired,
 * `decisions/0203` point 9; `decisions/0190` point 3,
 * `projects/turn-state-on-the-map.md` 1.5). It holds the table-wide state no token, inspector or
 * menu can show:
 *
 *   - **The Wings row (PLAY).** `LANCERS [PICK] n/m · ROUND n · n/m [PICK] HOSTILES`, for the GM and
 *     players alike. The counts are `turnRailLabel`, the domain `activationCountsOnSide` selector;
 *     PICK marks `activation.activeSide`; on the board, the picking side's READY chevrons glow.
 *   - **The lit wing (PLAY).** The picking side's half of the plate, from its slanted edge to the
 *     ROUND readout, fills with an animated gradient in the side's colour, opaque at the outer
 *     edge and fading out toward the middle: blue on the left for LANCERS, red on the right for HOSTILES (`projects/turn-presence.md` Phase 1). It follows
 *     `activeSide` while a unit is mid-turn or any able unit is still to act, and goes dark once
 *     the round can open, because `activeSide` keeps a value between rounds and nobody is picking.
 *     While a unit is mid-turn the lit wing names it, `▶ NAME` (Decision 1 C), on the wing's
 *     outer side (left of LANCERS, right of HOSTILES); a click pans the camera to it. A unit absent from the viewer's frame (GM-concealed) reads `▶ ACTING`: the wing
 *     already names its side, and the raw id is never shown.
 *   - **NEXT ROUND (GM, PLAY).** A smaller tab hanging below the round. Its legality and its press
 *     are `roundFlow`'s. While units still owe activations it reads `{n} TO ACT` and opens a list of
 *     them; a row selects the unit, pans to it and opens its inspector, where PASS lives. That list is
 *     the one PLAY route to an able unit that is not on the board, which would otherwise block the
 *     round.
 *   - **COMBAT OVER (PLAY).** Once the GM ends the fight (`end_combat`), the Wings row reads
 *     `COMBAT OVER · ROUND n` instead, nothing is lit and NEXT ROUND is gone. Ending also puts the
 *     table on Narrative, so this shows on a GM's preview of the ended fight or when the GM puts it
 *     back on the table (`decisions/0203` point 2).
 *   - **The SITREP row (either mode, when a sitrep is set).** What the SITREP strip under the top bar
 *     used to show, folded in by the developer's ruling of 2026-09-25. SITREP was mode-independent (a
 *     GM painting terrain in EDIT watches the clearance number move), so in EDIT the header shows this
 *     row alone, and nothing at all when no sitrep is set.
 *
 * It is an overlay, not a layout row: it costs the board no height and covers the board's top
 * centre, which the camera can pan away from. The header itself closes nothing and takes no Esc
 * entry; the "to act" list is a popover and registers in Esc layer 1 (`decisions/0189`).
 *
 * ## The SITREP row's one viewer-dependent input — read this before trusting either number
 *
 * `projectSitrepFor` (`packages/domain/src/encounter/projection.ts`) strips **the `hidden` FLAG off
 * every zone** for a non-GM viewer. It does **not** remove the zone: `id`, `kind` and `rects` all
 * survive into a player's frame (pinned by `projection.test.ts`'s *"player projection omits `hidden`
 * from every zone, while ids/kinds/rects survive"*). That is right for Recon: the players see all
 * four Control Zones and do not know which is the secretly designated True CZ. Two consequences:
 *
 *   - **The clearance numbers are identical for every viewer.** Geometry reaches everyone, so a
 *     player's header can never disagree with the GM's, and a hidden `clear`-kind zone cannot produce
 *     a false "100%" for the players.
 *   - **The `hidden` COUNT is GM-only** because the flag is the one thing redacted. A player's zones
 *     all read `hidden === undefined`, so the count renders for the GM alone — not because this
 *     component gates it on a role, but because the data is not in their frame.
 */
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../../lib/Icon";
import { useContext, useState } from "react";
import { activeActor, isCombatOver, perUnitState, sideOf, type CombatantId, type EncounterCommand, type EncounterState, type UnitActivationState } from "@lancer-vtt/domain";
import { clearancePercent, sitrepStatus, type SitrepClearance } from "@lancer-vtt/rules";
import { roundFlow } from "../../../lib/encounter/roundFlow";
import { turnRailLabel } from "../../../lib/encounter/turnChips";
import { EscLayersContext, useEscEntry } from "../../../lib/encounter/escLayers";

/** Display label per sitrep type — the header is read at a glance, so no bare wire values. */
const SITREP_LABEL: Record<NonNullable<EncounterState["sitrep"]>["type"], string> = {
  clear: "CLEAR THE LZ",
  control: "CONTROL",
  escort: "ESCORT",
  extraction: "EXTRACTION",
  gauntlet: "GAUNTLET",
  holdout: "HOLDOUT",
  recon: "RECON",
};

const STATE_LABEL: Record<UnitActivationState, string> = {
  ready: "READY",
  active: "ACTIVE",
  on_deck: "ON DECK",
  activated: "DONE",
};

export function RoundHeader({
  state,
  viewMode,
  isGm,
  actorId,
  dispatch,
  clearance,
  onFindUnit,
  onPanTo,
}: {
  state: EncounterState;
  viewMode: "edit" | "play";
  isGm: boolean;
  /** The viewer's slot (`""` before the socket binds one): NEXT ROUND sends as it. */
  actorId: string;
  dispatch: (command: EncounterCommand) => unknown;
  /** The `clear` sitrep's tally, derived once in `CombatApp` alongside the board's zone layer; `null`
   *  for every other sitrep type. Passed in rather than computed here because this component re-renders
   *  on every token drag, and the tally scans every zone cell against every combatant. */
  clearance: SitrepClearance | null;
  /** Select the unit, pan the camera to it and open its inspector (a "to act" row). */
  onFindUnit: (id: CombatantId) => void;
  /** Pan the camera to the unit, nothing else (the lit wing's active-unit name). */
  onPanTo: (id: CombatantId) => void;
}): React.JSX.Element | null {
  const play = viewMode === "play";
  const over = isCombatOver(state);
  const sitrep = state.sitrep ? <SitrepRow state={state} clearance={clearance} /> : null;
  if (!play && !sitrep) return null;

  return (
    <div className="combat-roundheader" data-testid="round-header">
      <div className={`combat-roundheader-plate${sitrep ? " has-sitrep" : ""}${play && !over ? litClass(state, actorId) : ""}`} data-testid="round-header-plate">
        {play && (over ? <CombatOverRow round={state.activation.round} /> : <WingsRow state={state} onPanTo={onPanTo} />)}
        {sitrep}
      </div>
      {play && isGm && !over && <NextRoundTab state={state} actorId={actorId} dispatch={dispatch} onFindUnit={onFindUnit} />}
    </div>
  );
}

/** The plate's lit-wing modifier: the picking side, unless the round is waiting on NEXT ROUND. */
function litClass(state: EncounterState, actorId: string): string {
  const picking = activeActor(state.activation) !== null || !roundFlow(state, actorId).canOpenRound;
  return picking ? ` is-lit-${state.activation.activeSide}` : "";
}

/** The plate once the fight is over: the turn flow is closed, so no wings, counts or pick. */
function CombatOverRow({ round }: { round: number }) {
  return (
    <div className="combat-roundheader-wings is-over" data-testid="round-header-over">
      <span className="combat-roundheader-round" data-testid="round-header-round">
        COMBAT OVER
        <small>AFTER ROUND {round}</small>
      </span>
    </div>
  );
}

function WingsRow({ state, onPanTo }: { state: EncounterState; onPanTo: (id: CombatantId) => void }) {
  const pick = state.activation.activeSide;
  const pickMark = <span className="combat-roundheader-pick">PICK</span>;
  const actor = activeActor(state.activation);
  const active = actor !== null && <ActiveUnit state={state} actor={actor} onPanTo={onPanTo} />;
  return (
    <div className="combat-roundheader-wings">
      <span className={`combat-roundheader-side is-pc${pick === "pc" ? " is-pick" : ""}`} data-testid="round-header-pc">
        {pick === "pc" && active}
        LANCERS {pick === "pc" && pickMark}
        <span className="combat-roundheader-count">{turnRailLabel(state, "pc")}</span>
      </span>
      <span className="combat-roundheader-round" data-testid="round-header-round">
        <small>ROUND</small>
        {state.activation.round}
      </span>
      <span className={`combat-roundheader-side is-npc${pick === "npc" ? " is-pick" : ""}`} data-testid="round-header-npc">
        <span className="combat-roundheader-count">{turnRailLabel(state, "npc")}</span>
        {pick === "npc" && pickMark} HOSTILES
        {pick === "npc" && active}
      </span>
    </div>
  );
}

/** The mid-turn unit's name in the lit wing; the side's own wing, since `activeSide` holds through the turn. */
function ActiveUnit({ state, actor, onPanTo }: { state: EncounterState; actor: CombatantId; onPanTo: (id: CombatantId) => void }) {
  const c = state.combatants[actor];
  if (!c) {
    return (
      <span className="combat-roundheader-active" data-testid="round-header-active">
        ▶ ACTING
      </span>
    );
  }
  return (
    <button type="button" className="combat-roundheader-active" data-testid="round-header-active" title={`${c.name} is acting. Click to pan to it.`} onClick={() => onPanTo(actor)}>
      ▶ {c.name}
    </button>
  );
}

function NextRoundTab({
  state,
  actorId,
  dispatch,
  onFindUnit,
}: {
  state: EncounterState;
  actorId: string;
  dispatch: (command: EncounterCommand) => unknown;
  onFindUnit: (id: CombatantId) => void;
}) {
  const flow = roundFlow(state, actorId);
  const [open, setOpen] = useState(false);
  const listable = !flow.canOpenRound && flow.toActUnits.length > 0;
  const showList = open && listable;

  // The list is a popover: an Esc layer-1 entry closes it (`decisions/0189`), and it closes itself
  // once nothing is left to list.
  useEscEntry(useContext(EscLayersContext), { layer: "popover", id: "round-header-to-act", active: showList, onDismiss: () => setOpen(false) });

  const states = showList ? perUnitState(state) : {};
  const empty = Object.keys(state.combatants).length === 0;
  return (
    <>
      <div className={`combat-roundheader-tabwrap${flow.canOpenRound ? " is-go" : ""}`}>
        {flow.canOpenRound ? (
          <button
            type="button"
            className="combat-roundheader-tab"
            data-testid="round-header-next"
            disabled={!actorId || empty}
            title="Next round: the side that did not act last goes first (RAW cross-round alternation)"
            onClick={() => {
              const cmd = flow.press();
              if (cmd) dispatch(cmd);
            }}
          >
            NEXT ROUND<Icon icon={faChevronRight} className="icon-trail" />
          </button>
        ) : (
          <button
            type="button"
            className="combat-roundheader-tab"
            data-testid="round-header-next"
            aria-expanded={showList}
            title="Every able unit must act before the round can end. Click to list them."
            onClick={() => setOpen((o) => !o)}
          >
            {flow.toAct} TO ACT<Icon icon={faChevronDown} className="icon-trail" />
          </button>
        )}
      </div>
      {showList && (
        <div className="combat-roundheader-toact" role="menu" data-testid="round-header-toact">
          <div className="combat-roundheader-toact-head">STILL TO ACT · CLICK TO FIND</div>
          {flow.toActUnits.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitem"
              className={`combat-roundheader-toact-row is-${sideOf(c)}`}
              data-combatant-id={c.id}
              onClick={() => {
                setOpen(false);
                onFindUnit(c.id);
              }}
            >
              <span className="combat-roundheader-toact-name">{c.name}</span>
              <span className="combat-roundheader-toact-state">{STATE_LABEL[states[c.id] ?? "on_deck"]}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function SitrepRow({ state, clearance }: { state: EncounterState; clearance: SitrepClearance | null }) {
  const sitrep = state.sitrep!;
  const status = sitrepStatus(state);
  const pct = clearance ? clearancePercent(clearance) : null;

  // The tracked combatant — Escort/Extraction's Objective, or the terraforming rig whose loss ends
  // the fight. Absent means destroyed or removed, which is what the predicate reads it as.
  const tracked = sitrep.objectiveId !== undefined ? state.combatants[sitrep.objectiveId] : undefined;
  const trackedLabel = sitrep.type === "clear" ? "RIG" : "OBJECTIVE";
  // Zones still carrying the `hidden` designation: always 0 in a player's frame (see the module doc).
  const concealedZones = sitrep.zones.filter((z) => z.hidden === true).length;

  const verdict =
    status.phase === "decided" ? (status.winner === "pc" ? "OBJECTIVE COMPLETE" : status.winner === "draw" ? "DRAW" : "OBJECTIVE FAILED") : null;
  // A DRAW takes neither tone: Escort/Extraction reach it when the objective is destroyed, and
  // colouring that as a loss would overstate it.
  const tone = verdict && status.winner === "pc" ? " is-won" : verdict && status.winner === "npc" ? " is-lost" : "";

  return (
    <div className={`combat-roundheader-sitrep${tone}`} role="region" aria-label="Sitrep" data-testid="round-header-sitrep">
      <span className="combat-roundheader-sitrep-type">SITREP · {SITREP_LABEL[sitrep.type]}</span>
      {clearance && pct !== null && clearance.total > 0 && (
        <>
          {/* The bar decorates the number beside it, never replaces it: a table reads digits aloud. */}
          <span className="combat-roundheader-sitrep-bar" aria-hidden>
            <span className="combat-roundheader-sitrep-bar-fill" style={{ width: `${pct}%` }} />
          </span>
          <span>
            <b>{clearance.clear}</b>/{clearance.total} CLEAR · {pct}%
          </span>
          <span className="is-quiet">{clearance.obstructed} TO GO</span>
        </>
      )}
      {clearance && clearance.total === 0 && <span className="is-quiet">NO LANDING ZONE AUTHORED</span>}
      {concealedZones > 0 && (
        <span className="combat-roundheader-sitrep-concealed">
          <b>{concealedZones}</b> GM-ONLY {concealedZones === 1 ? "ZONE" : "ZONES"}
        </span>
      )}
      {sitrep.objectiveId !== undefined && (
        <span className={tracked ? "is-quiet" : "combat-roundheader-sitrep-lost"}>
          {trackedLabel}: {tracked ? `${tracked.name} · ${tracked.current.hp}/${tracked.base.maxHp} HP` : "LOST"}
        </span>
      )}
      {/* `clear` has no round cap: `sitrepStatus` returns no `roundsRemaining` for it, and reading
          `undefined` as "0 rounds left" would invent a clock the engine does not run. */}
      {status.roundsRemaining !== undefined && (
        <span className="is-quiet">
          <b>{status.roundsRemaining}</b> RND LEFT
        </span>
      )}
      {status.scores && (
        <span className="is-quiet">
          PC <b>{status.scores.pc}</b> · NPC <b>{status.scores.npc}</b>
        </span>
      )}
      {verdict && <span className="combat-roundheader-sitrep-verdict">{verdict}</span>}
    </div>
  );
}
